import { useForm } from 'react-hook-form';
import { DialogActions, DialogContent, Grid, TextField } from '@mui/material';
import { Alert, AlertTitle } from '@mui/material';
import { KnownForceUpdateDeliveryDtoState } from '@groundfloor-platform/mgmt-service-g-sdk';
import { Modal } from '../../../../../ui/components/pure/modal/modal.component';
import { TextButton } from '../../../../../ui/components/pure/text-button/text-button.component';
import { Dropdown } from '../../../../../ui/components/pure/dropdown/dropdown.component';
import { FormControl } from '../../../../../ui/components/pure/form-control/form-control.component';
import { notePrivacyOptions } from '../../../../../ui/components/higher-order/add-note-action/add-note-action.constants';
import { parseErrorMessage } from '../../../../../core/api/api.context';
import { useHasPermissions } from '../../../../../core/permissions/has-permissions.hook';
import { Permissions } from '../../../../../core/permissions/permissions.typings';
import { Delivery } from '../../../models/delivery.model';
import { useMutationForceUpdateDelivery } from '../../deliveries-details.queries';
import {
  deliveryAgeInDays,
  FORCE_UPDATE_MINIMUM_AGE_DAYS,
  meetsForceUpdateMinimumAge
} from '../deliveries-details.actions';

const forceUpdateStateOptions: { label: string; value: string }[] = [
  { label: 'Abandoned - the delivery never happened', value: KnownForceUpdateDeliveryDtoState.Abandoned },
  { label: 'Collected - the recipient has the item', value: KnownForceUpdateDeliveryDtoState.Collected },
  { label: 'Retrieved - the item was taken back', value: KnownForceUpdateDeliveryDtoState.Retrieved }
];

interface FormData {
  state: string;
  visibility: string;
  note: string;
}

export function ForceUpdateModal({
  delivery,
  onAbort,
  onSuccess
}: {
  delivery: Delivery;
  onAbort(): void;
  onSuccess(): void;
}) {
  if (!meetsForceUpdateMinimumAge(delivery)) {
    return <MinimumAgeNotMet delivery={delivery} onClose={onAbort} />;
  }

  return <ForceUpdateForm delivery={delivery} onAbort={onAbort} onSuccess={onSuccess} />;
}

/**
 * A delivery under the minimum age gets the action button, but the modal explains
 * why it cannot be forced yet instead of offering the form.
 */
function MinimumAgeNotMet({ delivery, onClose }: { delivery: Delivery; onClose(): void }) {
  const ageInDays = Math.floor(deliveryAgeInDays(delivery));

  return (
    <Modal open={true} title="Force update" maxWidth="sm" onClose={onClose}>
      <DialogContent style={{ padding: '16px 24px', fontSize: '14px' }}>
        <p>
          {`This delivery is ${ageInDays} ${
            ageInDays === 1 ? 'day' : 'days'
          } old. A delivery must be more than ${FORCE_UPDATE_MINIMUM_AGE_DAYS} days old before its state can be forced.`}
        </p>
        <p>
          {`This is to allow time for events to sync to the cloud before cleaning up. A delivery this recent may still settle on its own once the kiosk's events arrive.`}
        </p>
      </DialogContent>
      <DialogActions style={{ borderTop: '1px solid #EBEDF2', padding: '16px 24px' }}>
        <TextButton color="secondary" variant="outlined" onClick={onClose}>
          Close
        </TextButton>
      </DialogActions>
    </Modal>
  );
}

/**
 * The form stays open on failure so the operator can read the reason: a delivery
 * that still holds a locker, or one migrated from AC, has to be escalated, and
 * the backend's message is the only place that is explained.
 */
function ForceUpdateForm({ delivery, onAbort, onSuccess }: { delivery: Delivery; onAbort(): void; onSuccess(): void }) {
  const privateNotePermission = useHasPermissions([
    { action: Permissions.notes.createPrivate, projectID: delivery.project.id }
  ]);
  const canCreatePrivateNote = privateNotePermission.hasSomePermissions;

  const { mutate, error, isLoading } = useMutationForceUpdateDelivery();

  const { handleSubmit, control } = useForm<FormData>({
    defaultValues: {
      state: '',
      visibility: canCreatePrivateNote ? 'private' : 'public',
      note: ''
    }
  });

  const onSubmit = (data: FormData) => {
    mutate(
      {
        deliveryID: delivery.id,
        state: data.state,
        note: data.note,
        isPrivate: data.visibility === 'private'
      },
      { onSuccess }
    );
  };

  return (
    <Modal open={true} loading={isLoading} title="Force update" maxWidth="sm" onClose={onAbort}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent style={{ padding: '16px 24px', fontSize: '14px' }}>
          {error ? (
            <Alert variant="outlined" severity="error" style={{ marginBottom: '1rem' }}>
              <AlertTitle>The delivery could not be updated</AlertTitle>
              {parseErrorMessage(error.message)}
            </Alert>
          ) : null}
          <p>
            {`This overrides the delivery's state without running the normal transition. No notification is sent to the recipient and no locker is opened.`}
          </p>
          <Grid style={{ paddingBottom: '1rem' }}>
            <FormControl
              control={control}
              name="state"
              ComponentProps={{
                fullWidth: true,
                required: true,
                textFieldDesktopWidth: '100%',
                dropdownVariant: 'primarySolid' as const,
                placeholder: 'Select the final state',
                menuItems: forceUpdateStateOptions
              }}
              Component={Dropdown}
            />
          </Grid>
          {canCreatePrivateNote ? (
            <Grid style={{ paddingBottom: '1rem' }}>
              <FormControl
                control={control}
                name="visibility"
                ComponentProps={{
                  fullWidth: true,
                  required: true,
                  textFieldDesktopWidth: '100%',
                  dropdownVariant: 'primarySolid' as const,
                  placeholder: 'Select privacy',
                  menuItems: notePrivacyOptions
                }}
                Component={Dropdown}
              />
            </Grid>
          ) : null}
          <Grid>
            <FormControl
              control={control}
              name="note"
              ComponentProps={{
                autoFocus: true,
                fullWidth: true,
                multiline: true,
                required: true,
                minRows: 5,
                type: 'text',
                variant: 'outlined' as const,
                placeholder: 'Why is this delivery being force updated?',
                style: { width: '100%' }
              }}
              Component={TextField}
            />
          </Grid>
        </DialogContent>
        <DialogActions style={{ borderTop: '1px solid #EBEDF2', padding: '16px 24px' }}>
          <TextButton color="secondary" variant="outlined" onClick={onAbort}>
            Cancel
          </TextButton>
          <TextButton color="danger" variant="outlined" type="submit">
            Force update
          </TextButton>
        </DialogActions>
      </form>
    </Modal>
  );
}
